// GestionEvaluationsClient.jsx - Version avec pagination et filtres
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import DemoViewer from '../../components/communs/DemoViewer';


import {
  ClipboardList, Eye, Clock, CheckCircle,
  XCircle, AlertCircle, Loader2, RefreshCw,
  Calendar, User, Award, BookOpen, ChevronLeft, ChevronRight
} from 'lucide-react';

export default function GestionEvaluationsClient() {
  const { token, user, isAuthenticated } = useAuth();
  const clientId = user?.clientId || user?.client?.id || user?.id;

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Pagination et filtres
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0
  });
  const [filters, setFilters] = useState({
    type: '',
    statut: ''
  });

  const [showEvalDemo, setShowEvalDemo] = useState(false);
  const [demoEvalData, setDemoEvalData] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const fetchEvaluations = useCallback(async (page = 0, type = '', statut = '') => {
    if (!token || !isAuthenticated || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('size', pagination.size);
      if (type) params.set('type', type);
      if (statut) params.set('statut', statut);

      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
      );
      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
      const data = await response.json();
      const evalList = data.content || data.data || data || [];
      setEvaluations(evalList);
      setPagination(prev => ({
        ...prev,
        page: data.number || page,
        totalPages: data.totalPages || 0,
        totalElements: data.totalElements || evalList.length
      }));
    } catch (err) {
      console.error('Erreur récupération évaluations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, isAuthenticated, clientId, pagination.size]);

  useEffect(() => {
    fetchEvaluations(0, filters.type, filters.statut);
  }, [fetchEvaluations]); // on ne met pas filters dans les dépendances pour éviter boucle, on déclenche manuellement

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    await fetchEvaluations(pagination.page, filters.type, filters.statut);
  }, [fetchEvaluations, pagination.page, filters.type, filters.statut]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      fetchEvaluations(newPage, filters.type, filters.statut);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Recharger avec les nouveaux filtres à la page 0
    fetchEvaluations(0, key === 'type' ? value : filters.type, key === 'statut' ? value : filters.statut);
  };

  const fetchEvaluationQuestions = useCallback(async (evaluationId) => {
    try {
      const endpoint = `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations/${evaluationId}/questions`;
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        let questions = [];
        if (data.questions && Array.isArray(data.questions)) questions = data.questions;
        else if (data.data && data.data.questions && Array.isArray(data.data.questions)) questions = data.data.questions;
        else if (Array.isArray(data)) questions = data;
        return questions;
      }
      return [];
    } catch (err) {
      console.error('Erreur récupération questions:', err);
      return [];
    }
  }, [token]);

  const openEvalDemo = useCallback(async (evaluation) => {
    setLoadingQuestions(true);
    try {
      let questions = [];
      if (evaluation.questions && Array.isArray(evaluation.questions) && evaluation.questions.length > 0) {
        questions = evaluation.questions;
      } else if (evaluation.id) {
        questions = await fetchEvaluationQuestions(evaluation.id);
      }
      if (questions.length === 0) {
        alert("⚠️ Cette évaluation ne contient aucune question.");
        setLoadingQuestions(false);
        return;
      }
      const demoData = {
        isDirectEvaluation: true,
        formationId: null,
        module: {
          id: evaluation.id || 'eval-demo',
          titre: evaluation.titre || 'Évaluation sans titre',
          description: evaluation.description || '',
          type: 'EVALUATION',
          typeModule: 'EVALUATION',
          ordre: 0,
          formationId: null,
          evalType: evaluation.type || evaluation.evalType || 'SOMMATIVE',
          questions: questions,
          evaluations: questions,
          content: questions
        },
        content: {
          type: 'EVALUATION',
          data: {
            id: evaluation.id,
            titre: evaluation.titre,
            description: evaluation.description,
            type: evaluation.type || evaluation.evalType || 'SOMMATIVE',
            questions: questions,
            scoreMinimum: evaluation.scoreMinimum || 70
          },
          questions: questions,
          evaluations: questions
        },
        token,
        mode: 'demo'
      };
      setDemoEvalData(demoData);
      setShowEvalDemo(true);
      setLoadingQuestions(false);
    } catch (err) {
      console.error('Erreur ouverture démo:', err);
      alert("❌ Erreur lors du chargement des questions: " + err.message);
      setLoadingQuestions(false);
    }
  }, [token, fetchEvaluationQuestions]);

  const getStatusInfo = (statut) => {
    const map = {
      'EN_ATTENTE': { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
      'PENDING': { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
      'EN_COURS': { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Clock },
      'IN_PROGRESS': { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Clock },
      'BROUILLON': { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: AlertCircle },
      'TERMINE': { label: 'Terminée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'COMPLETED': { label: 'Terminée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'REALISEE': { label: 'Réalisée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'REJETEE': { label: 'Rejetée', color: 'bg-red-100 text-red-700', icon: XCircle },
      'REJECTED': { label: 'Rejetée', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    return map[statut?.toUpperCase()] || { label: statut || 'Inconnu', color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
  };

  const getTypeInfo = (type) => {
    const map = {
      'SOMMATIVE': { label: 'Sommatif', color: 'bg-purple-100 text-purple-700' },
      'SOMATIVE': { label: 'Sommatif', color: 'bg-purple-100 text-purple-700' },
      'FORMATIVE': { label: 'Formatif', color: 'bg-blue-100 text-blue-700' },
      'DIAGNOSTIC': { label: 'Diagnostique', color: 'bg-emerald-100 text-emerald-700' }
    };
    return map[type?.toUpperCase()] || { label: type || 'Inconnu', color: 'bg-gray-100 text-gray-700' };
  };

  const changeStatus = async (evaluationId, newStatus) => {
    try {
      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations/${evaluationId}/statut?statut=${newStatus}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
      if (response.ok) {
        await refreshData();
      } else {
        const errorData = await response.json();
        console.error('Erreur changement de statut:', errorData);
        alert(`Erreur: ${errorData.message || 'Changement de statut échoué'}`);
      }
    } catch (err) {
      console.error('Erreur changement de statut:', err);
      alert('Erreur réseau');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-8 text-center">
        <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button onClick={refreshData} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
          <RefreshCw size={16} className="inline mr-2" /> Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList size={20} className="text-purple-600" />
            <h3 className="text-sm font-bold text-slate-700">
              Évaluations
              <span className="ml-2 text-xs font-normal text-slate-400">
                ({pagination.totalElements || evaluations.length} évaluation{pagination.totalElements > 1 ? 's' : ''})
              </span>
            </h3>
          </div>
          <button onClick={refreshData} disabled={refreshing} className="px-3 py-1.5 text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg transition flex items-center gap-1.5">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Rafraîchir
          </button>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
          >
            <option value="">Tous les types</option>
            <option value="SOMMATIVE">Sommatif</option>
            <option value="FORMATIVE">Formatif</option>
            <option value="DIAGNOSTIC">Diagnostique</option>
          </select>
          <select
            value={filters.statut}
            onChange={(e) => handleFilterChange('statut', e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
          >
            <option value="">Tous les statuts</option>
            <option value="BROUILLON">Brouillon</option>
            <option value="PUBLIEE">Publiée</option>
            <option value="EN_COURS">En cours</option>
            <option value="TERMINE">Terminée</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {evaluations.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">Aucune évaluation</p>
            <p className="text-xs text-slate-400 mt-1">Ajustez vos filtres ou créez une évaluation.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {evaluations.map((evalItem) => {
              const statusInfo = getStatusInfo(evalItem.statut);
              const typeInfo = getTypeInfo(evalItem.type || evalItem.evalType);
              const StatusIcon = statusInfo.icon;
              const hasQuestions = (evalItem.nombreQuestions || 0) > 0;
              const canPreview = hasQuestions || evalItem.questions?.length > 0 || evalItem.content?.length > 0;

              return (
                <div key={evalItem.id} className="px-4 py-3 hover:bg-slate-50 transition flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{evalItem.titre || 'Sans titre'}</span>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${typeInfo.color}`}>{typeInfo.label}</span>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${statusInfo.color} flex items-center gap-1`}>
                        <StatusIcon size={10} /> {statusInfo.label}
                      </span>
                      {!canPreview && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">⚠️ Sans questions</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400 flex-wrap">
                      {evalItem.formation?.titre && <span className="flex items-center gap-1"><BookOpen size={11} /> {evalItem.formation.titre}</span>}
                      {evalItem.etudiant?.nom && <span className="flex items-center gap-1"><User size={11} /> {evalItem.etudiant.prenom} {evalItem.etudiant.nom}</span>}
                      {evalItem.note !== undefined && evalItem.note !== null && <span className="flex items-center gap-1 text-emerald-600 font-bold"><Award size={11} /> {evalItem.note}/20</span>}
                      {evalItem.dateCreation && <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(evalItem.dateCreation).toLocaleDateString('fr-FR')}</span>}
                      {evalItem.nombreQuestions > 0 && <span className="text-[9px] text-slate-400">{evalItem.nombreQuestions} question(s)</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => canPreview && openEvalDemo(evalItem)}
                      disabled={!canPreview || loadingQuestions}
                      className={`p-2 rounded-lg transition ${canPreview && !loadingQuestions ? 'text-indigo-600 hover:bg-indigo-50' : 'text-gray-300 cursor-not-allowed'}`}
                      title={canPreview ? "Prévisualiser" : "Aucune question"}
                    >
                      {loadingQuestions ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
                    </button>
                    <select
                      value={evalItem.statut || 'EN_ATTENTE'}
                      onChange={(e) => changeStatus(evalItem.id, e.target.value)}
                      className="text-[10px] border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                    >
                      <option value="BROUILLON">Brouillon</option>
                      <option value="PUBLIEE">Publiée</option>
                      <option value="TERMINEE">Terminée</option>
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
          <span>
            Page {pagination.page + 1} / {pagination.totalPages} ({pagination.totalElements} éléments)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 0}
              className="p-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-30 transition"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages - 1}
              className="p-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-30 transition"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {showEvalDemo && demoEvalData && (
        <DemoViewer
          formationId={null}
          token={demoEvalData.token || token}
          onClose={() => { setShowEvalDemo(false); setDemoEvalData(null); }}
          isOpen={showEvalDemo}
          userRole="client"
          initialModule={demoEvalData.module}
          initialContent={demoEvalData.content}
          isDirectEvaluation={true}
        />
      )}
    </div>
  );
}